import React, { useId } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import Lottie from 'lottie-react';
import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';
import { countryCode, historicalCountryName, birthConflictPresentation } from '../../../utils/identityContext';
import { nameMatchDisplayLabel } from '../../../utils/nameMatchDisplay';
import iconName from '../../../images/individual-lottie.json';
import iconBirth from '../../../images/birthday.svg';
import iconNationality from '../../../images/passport.svg';
import iconDomicile from '../../../images/earth-location.svg';
import './IdentityContext.css';

countries.registerLocale(en);

const icons = { dob: iconBirth, nationality: iconNationality, domicile: iconDomicile };
function ContextIcon({ field }) {
  return <span className="identityContextIcon" aria-hidden="true">{field === 'name'
    ? <Lottie animationData={iconName} autoplay={false} loop={false} />
    : <img src={icons[field]} alt="" />}</span>;
}
function displayContextValues(field, values) {
  if (!['nationality', 'domicile'].includes(field)) return values?.join(' · ');
  return [...new Set((values || []).map(value => {
    const historical = historicalCountryName(value);
    if (historical) return `${historical} (historical)`;
    const code = countryCode(value);
    return (code && countries.getName(code, 'en', { select: 'alias' })) || value;
  }))].join(' · ');
}
const labels = { dob: 'DOB', nationality: 'Nationality', domicile: 'Domicile' };
const tone = status => ['exact','year','partial','range','estimate','match'].includes(status) ? 'match' : status === 'conflict' ? 'conflict' : ['differs','uncertain'].includes(status) ? 'difference' : 'neutral';

function ContextBadge({ field, label, value, status = 'neutral', displayTone, children }) {
  const id = useId();
  return <div className="identityContextField" data-tone={displayTone || tone(status)}>
    <button type="button" className="identityContextTrigger" aria-label={`${label}: ${value}`} data-tooltip-id={id} aria-describedby={id}>
      <span className="identityContextLabel"><ContextIcon field={field} /><span>{label}</span></span><strong>{value}</strong>
    </button>
    {createPortal(<Tooltip id={id} place="top" positionStrategy="fixed" className="identityContextTooltip" opacity={1}
      clickable delayShow={150} delayHide={120} globalCloseEvents={{ escape: true, scroll: true, resize: true, clickOutsideAnchor: true }}>
      <div className="identityContextTooltipTitle">
        <span className="identityContextTooltipIcon"><ContextIcon field={field} /></span>
        <span className="identityContextTooltipHeading">{label}</span>
        <span className="identityContextTooltipStatus" data-tone={displayTone || tone(status)}>{value}</span>
      </div>
      <div className="identityContextDetail">{children}</div>
    </Tooltip>, document.body)}
  </div>;
}

export function ContextLimitation({ context }) {
  const id = useId();
  if (!context?.limitation) return null;
  const icon = context.limitation === 'Uncertain context' ? 'bi-question-circle' : 'bi-info-circle';
  const limitedFields = Object.entries(context.fields || {}).filter(([, value]) => !['exact', 'match', 'differs', 'conflict'].includes(value.status));
  const precisionDetails = {
    year: 'The birth year agrees, but a complete date is not available on both sides.',
    partial: 'The available birth date components agree, but the full date cannot be compared.',
    range: 'The birth information falls within a listed range; an exact date cannot be confirmed.',
    estimate: 'The birth information is compatible with an estimate rather than a confirmed exact date.',
  };
  return <>
    <button type="button" className="identityContextLimitation" data-tooltip-id={id} aria-describedby={id}
      onClick={event => event.stopPropagation()}>
      <i className={`bi ${icon}`} aria-hidden="true" />{context.limitation}
    </button>
    {createPortal(<Tooltip id={id} place="top" positionStrategy="fixed" className="identityContextTooltip" opacity={1}
      clickable delayShow={150} delayHide={120} globalCloseEvents={{ escape: true, scroll: true, resize: true, clickOutsideAnchor: true }}>
      <div onClick={event => event.stopPropagation()}>
        <div className="identityContextTooltipTitle">
          <span className="identityContextTooltipIcon"><i className={`bi ${icon}`} aria-hidden="true" /></span>
          <span className="identityContextTooltipHeading">{context.limitation}</span>
        </div>
        <div className="identityContextDetail">
          {limitedFields.map(([key, value]) => <div className="identityContextLimitationDetail" key={key}>
            <div className="identityContextLimitationDetailTitle">
              <ContextIcon field={key} /><b>{value.ageEvidence ? 'Age evidence' : value.displayName || labels[key]}</b>
              <span className="identityContextTooltipStatus" data-tone={tone(value.status)}>{value.label}</span>
            </div>
            <p>{precisionDetails[value.status] || value.explanation}</p>
          </div>)}
        </div>
      </div>
    </Tooltip>, document.body)}
  </>;
}

export function MissingContextNote({ meta = {}, roles = [], readOnly = false }) {
  if (meta.subjectType && !['individual','person'].includes(meta.subjectType)) return null;
  const fields = [['dateOfBirth','date of birth'],['nationality','nationality'],['domicileCountry','domicile']].filter(([key]) => !meta[key]);
  if (!fields.length) return null;
  const edit = () => {
    const role = roles.join(' ').toLowerCase();
    const title = /client|customer/.test(role) ? /customer identification/i : /signator/.test(role) ? /authorised signatories/i : /ownership.*control/i;
    const heading = [...document.querySelectorAll('.kycSectionTitle')].find(el => title.test(el.textContent));
    const card = heading?.closest('.kycCard');
    if (heading) {
      heading.scrollIntoView({behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block:'start'});
      heading.setAttribute('tabindex','-1');
      heading.focus({preventScroll:true});
      card?.classList.add('identityContextEditTarget');
      window.setTimeout(() => card?.classList.remove('identityContextEditTarget'), 1800);
    }
  };
  return <div className="identityContextMissing"><i className="bi bi-info-circle" aria-hidden="true" /><span>{readOnly ? 'Missing from KYC: ' : 'Add '}{fields.map(([,label]) => label).join(', ')}{readOnly ? '' : ' in the subject’s KYC details to improve identity assessment'}</span>{!readOnly && <button type="button" onClick={edit}>Edit KYC details <i className="bi bi-arrow-up-right" aria-hidden="true" /></button>}</div>;
}

export default function IdentityContext({ context, strength, nameReason, screenedName, comparedName, onRetry }) {
  return <div className="identityContext" onClick={e => e.stopPropagation()}>
    <div className="identityContextSummary">Context identity assessment</div>
    <div className="identityContextFields">
      <ContextBadge field="name" label="Name" status={strength === 'strong' ? 'match' : strength === 'moderate' ? 'uncertain' : 'neutral'} value={nameMatchDisplayLabel(strength, screenedName, comparedName)}>
        <p className="identityContextExplanation">{nameReason || 'The existing name-only assessment is unchanged.'}</p>
      </ContextBadge>
      {Object.entries(context?.fields || {}).map(([key, value]) => {
        const presentation = key === 'dob' ? birthConflictPresentation(value) : null;
        return <ContextBadge key={key} field={key}
        label={value.ageEvidence ? 'Age evidence' : value.displayName || labels[key]} status={value.status}
        displayTone={presentation?.tone}
        value={presentation?.label || (value.ageEvidence && value.status === 'conflict' ? 'Incompatible' : value.status === 'match' ? 'Consistent' : value.label)}>
        <div className="identityContextComparisonRow"><b>KYC</b><span>{displayContextValues(key, value.subject) || 'Not provided'}</span></div>
        <div className="identityContextComparisonRow"><b>Source</b><span>{displayContextValues(key, value.source) || 'Not listed'}</span></div>
        <p className="identityContextExplanation">{presentation?.explanation || value.explanation}</p>
      </ContextBadge>;
      })}
      {context?.status === 'unavailable' && onRetry ? <button type="button" className="btn btn-sm btn-link" onClick={onRetry}>Retry comparison</button> : null}
    </div>
  </div>;
}
